import {UndefinedChangeHistoryEntryError} from "@buildwithflux/core";
import {IActionRecord, SurfaceBasedTrackingEvents, TrackingEventActions} from "@buildwithflux/models";
import {isProductionEnvironment} from "@buildwithflux/shared";
import {captureMessage} from "@sentry/react";
import {FluxServices} from "export";

import {IChangeHistoryData} from "modules/changeHistory/state/types";
import {snackbarMessageHelper} from "modules/common/providers/FluxSnackbarProvider";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import PerformanceLogger from "modules/storage_engine/connectors/PerformanceLogger";
import R from "resources/Namespace";

function getActionRecordsBetweenTwoActionRecords(
    changeHistoryState: IChangeHistoryData,
    actionRecordBegin: IActionRecord,
    actionRecordEnd: IActionRecord,
): IActionRecord[] {
    const actionRecords = new Array<IActionRecord>();

    let goBackInVersion = false;
    let timestampBegin = 0;
    let timestampEnd = 0;
    if (actionRecordBegin && actionRecordEnd) {
        timestampBegin = actionRecordBegin.actionTimestamp;
        timestampEnd = actionRecordEnd.actionTimestamp;
        if (timestampBegin > timestampEnd) {
            goBackInVersion = true;
        }
    }

    if (changeHistoryState) {
        for (const record of Object.values(changeHistoryState?.changeHistoryEntries)) {
            if (goBackInVersion && record.actionTimestamp <= timestampBegin && record.actionTimestamp > timestampEnd) {
                actionRecords.push(record);
            } else if (
                !goBackInVersion &&
                record.actionTimestamp > timestampBegin &&
                record.actionTimestamp <= timestampEnd
            ) {
                actionRecords.push(record);
            }
        }
    }

    if (goBackInVersion) {
        actionRecords.sort((a, b) => {
            if (a.actionTimestamp > b.actionTimestamp) {
                return -1;
            } else {
                return 1;
            }
        });
    } else {
        actionRecords.sort((a, b) => {
            if (a.actionTimestamp < b.actionTimestamp) {
                return -1;
            } else {
                return 1;
            }
        });
    }

    return actionRecords;
}

function _revertOrForwardChanges(
    changeHistoryState: IChangeHistoryData,
    targetChangeId: string,
    services: FluxServices,
    clearCurrentChangeId?: boolean,
): boolean | undefined {
    // NOTE: Empty string is a valid state for resetting the change history.
    if (!targetChangeId) {
        return undefined;
    }
    const {currentChangeId, requestedChangeId} = changeHistoryState;
    const trace = PerformanceLogger.getInstance().startPerformanceTrace("ChangeHistoryContext.revertOrForwardChange");
    const requestedChange = changeHistoryState.changeHistoryEntries[targetChangeId];
    if (!requestedChange) {
        FluxLogger.captureError(new UndefinedChangeHistoryEntryError(targetChangeId, "requestedChange"));
        return false;
    }
    const currentChange = changeHistoryState.changeHistoryEntries[currentChangeId];
    if (!currentChange) {
        FluxLogger.captureError(new UndefinedChangeHistoryEntryError(currentChangeId, "currentChange"));
        return false;
    }

    if (requestedChange.actionCodeVersion !== currentChange.actionCodeVersion) {
        const warning = `Restoring document data to a version that was created by a different code version ${requestedChange.actionCodeVersion} than the current code version ${currentChange.actionCodeVersion}. Expect the unexpected.`;
        // TODO: ideally, we'd use SentryErrorReporter in frontend services also
        captureMessage(warning, {level: "warning"});
        if (!isProductionEnvironment()) {
            // eslint-disable-next-line no-console
            console.warn(warning);
        }
    }

    const actionRecords = getActionRecordsBetweenTwoActionRecords(changeHistoryState, currentChange, requestedChange);
    if (currentChange.actionTimestamp > requestedChange.actionTimestamp) {
        // NOTE: this should be atomic
        services.documentStore.getState().revertActionRecords(actionRecords);
    } else {
        services.documentStore.getState().applyActionRecords(actionRecords);
    }
    if (!clearCurrentChangeId) {
        // Update Zustand changeHistory store (Redux changeHistory slice has been removed)
        services.changeHistoryStore.getState().setCurrentChangeId(requestedChange.actionRecordUid);
    } else if (clearCurrentChangeId) {
        // Update Zustand changeHistory store (Redux changeHistory slice has been removed)
        services.changeHistoryStore.getState().setCurrentChangeId("");
    }
    PerformanceLogger.getInstance().stopPerformanceTrace(trace);

    return true;
}

export const actionCreators = {
    /**
     * Restores the document to the currently selected version in the change history panel.
     * This is an async function that takes FluxServices directly (not a Redux thunk).
     */
    restoreDocumentVersion: async (services: FluxServices): Promise<void> => {
        // Read from Zustand store instead of Redux to get the latest state
        const changeHistoryState = services.changeHistoryStore.getState();
        const {currentChangeId} = changeHistoryState;
        const trace = PerformanceLogger.getInstance().startPerformanceTrace(
            "ChangeHistoryContext.restoreDocumentVersion",
        );
        const latestChangeUid = changeHistoryState?.changeHistoryCacheMetadata.topActionRecordUid;
        if (latestChangeUid && latestChangeUid !== currentChangeId) {
            const latestChange = changeHistoryState?.changeHistoryEntries[latestChangeUid];
            const currentChange = changeHistoryState?.changeHistoryEntries[currentChangeId];
            if (currentChange && latestChange) {
                const actionRecords = getActionRecordsBetweenTwoActionRecords(
                    changeHistoryState,
                    latestChange,
                    currentChange,
                );
                /**
                 * Here, this `_revertOrForwardChanges`, is to _make sure that the document is in latest version before doing any revert_.
                 *
                 * Here's what happens when user tries to restore to a previous version (i.e. revert document to a previous action record):
                 * 1. User first needs to select the targeted change, which will trigger the `revertOrForwardChanges` below
                 *   - Note that this action also calls the `_revertOrForwardChanges` function, which will update the document
                 *     to that targeted change so that user can see it in Editor. However, this will _NOT_ do any server request
                 *     so all of these are just local
                 * 2. Then when user decides to restore, hit the "Restore Version" button, we will:
                 *   a. First calling `_revertOrForwardChanges` to re-apply action records to the latest one, so that our local doc is
                 *      now latest
                 *   b. Then dispatch the `revertDocumentVersion` action, which if you looked at the reducer, it is doing the exact
                 *      same job as `revertActionRecords` action does. But `revertActionRecords` will trigger a server request, generate
                 *      diff, etc.
                 *
                 * That's why it is so important that step 2a) is needed... otherwise, skipping 2a) and executing 2b) directly will
                 * generate an action record with empty diff (!!!), because the document is already reverted in step#1.
                 *
                 * TODO: We should definitely improve this... because we are essentially updating the document data back
                 * and forth to execute one simple job. See failed refactor in https://github.com/buildwithflux/flux-app/pull/12428.
                 */
                let success: boolean | undefined = false;
                try {
                    success = _revertOrForwardChanges(changeHistoryState, latestChange.actionRecordUid, services);
                    services.documentStore.getState().revertDocumentVersion(actionRecords);

                    // --- Custom Restore Logic for Markdown Docs ---
                    // Since markdown docs are not part of IDocumentData patches, we must manually
                    // calculate their target state and update the repository.
                    // We iterate "backwards" (Undo) from Latest to Target.
                    try {
                        const {projectDocRepository, documentStore} = services;
                        const documentUid = documentStore.getState().document?.uid;

                        if (documentUid) {
                            // 1. Identify touched docs
                            const touchedDocs = new Set<string>();
                            const markdownDiffs: any[] = [];

                            for (const record of actionRecords) {
                                if (Array.isArray(record.actionChangeDiff)) {
                                    for (const d of record.actionChangeDiff) {
                                        if (d.path && d.path[0] === "markdownDocs" && d.path[1]) {
                                            touchedDocs.add(d.path[1]);
                                            markdownDiffs.push({record, diff: d});
                                        }
                                    }
                                }
                            }

                            // 2. Process each doc
                            for (const docUid of touchedDocs) {
                                // Fetch current state
                                let currentSnapshot = {content: "", name: ""};
                                try {
                                    const liveDoc = await projectDocRepository.getProjectDoc(documentUid, docUid);
                                    if (liveDoc) {
                                        currentSnapshot = {content: liveDoc.content, name: liveDoc.name};
                                    }
                                } catch (e) {
                                    // Ignore if not found (might be deleted currently)
                                }

                                let exists = true; // Assume exists unless we prove otherwise (or if get failed? no, if get failed it might not exist)
                                // Better: track existence based on what we fetched?
                                // If fetch threw or returned null, exists = false.
                                // But let's refine:
                                try {
                                    const liveDoc = await projectDocRepository.getProjectDoc(documentUid, docUid);
                                    exists = !!liveDoc;
                                } catch {
                                    exists = false;
                                }

                                let targetContent = currentSnapshot.content;
                                let targetName = currentSnapshot.name;
                                let targetExists = exists;

                                // Apply Undos (Iterate records in order, assuming Descending/Latest-First)
                                // actionRecords is sorted Descending by _getActionRecordsBetweenTwoActionRecords when going back.
                                for (const {record, diff} of markdownDiffs) {
                                    if (diff.path[1] !== docUid) continue;

                                    // Undo logic
                                    if (diff.kind === "N") {
                                        // Was Created -> Undo = Delete
                                        targetExists = false;
                                    } else if (diff.kind === "D") {
                                        // Was Deleted -> Undo = Create (restore LHS)
                                        targetExists = true;
                                        targetContent = diff.lhs.content;
                                        targetName = diff.lhs.name;
                                    } else if (diff.kind === "E") {
                                        // Was Edited -> Undo = Set to LHS
                                        targetContent = diff.lhs.content;
                                        if (diff.lhs.name) targetName = diff.lhs.name;
                                    }
                                }

                                // 3. Execute Net Op

                                if (targetExists && !exists) {
                                    // Create
                                    await projectDocRepository.createProjectDoc(documentUid, {
                                        uid: docUid,
                                        document_uid: documentUid,
                                        name: targetName,
                                        content: targetContent,
                                        created_at: Date.now(),
                                        updated_at: Date.now(),
                                    });
                                    services.documentStore
                                        .getState()
                                        .createProjectDoc(docUid, targetName, targetContent);
                                } else if (!targetExists && exists) {
                                    // Delete
                                    await projectDocRepository.deleteProjectDoc(documentUid, docUid);
                                    services.documentStore
                                        .getState()
                                        .deleteProjectDoc(docUid, targetName, targetContent);
                                } else if (targetExists && exists) {
                                    // Update if changed
                                    if (
                                        targetContent !== currentSnapshot.content ||
                                        targetName !== currentSnapshot.name
                                    ) {
                                        await projectDocRepository.updateProjectDoc(documentUid, {
                                            uid: docUid,
                                            content: targetContent,
                                            name: targetName,
                                            updated_at: Date.now(),
                                        });
                                        // Pass current as previousContent
                                        services.documentStore
                                            .getState()
                                            .updateProjectDoc(
                                                docUid,
                                                targetName,
                                                targetContent,
                                                currentSnapshot.content,
                                            );
                                    }
                                }
                            }
                        }
                    } catch (err) {
                        if (err instanceof Error) {
                            err.message = `Failed to restore markdown docs: ${err.message}`;
                        }
                        FluxLogger.captureError(err);
                        // Don't block main restore?? Or should we?
                        // PRD implies it's part of the feature. But failure shouldn't crash the app.
                        // We log it.
                    }

                    // Hide change history panel when restoring.
                    // NOTE: This is very disconnected so just wanna document it:
                    // this sets `isVersionHistoryMode` to false, which then dispatches action
                    // to set requestedChangeId to "" in `useChangeHistory.tsx`
                    // TODO: The whole setup of change history in both data and UI is very brittle. We should
                    // have a service layer to manage OR orchestrate the ui state and the underlying data/interaction
                    services.useDocumentUiStore.getState().hideChangeHistoryPanel();
                } catch (error1) {
                    // If the sequence of action records fails, try with the
                    // snapshots. Why not just use snapshots? Historically, the
                    // change history panel was designed to use action records
                    // only, and it's not clear what bugs might be introduced by
                    // using snapshots only and some manual testing has already
                    // revealed some bugs. Why not just use action records then?
                    // We need snapshots when action records fail and we need to
                    // reconstruct the project server-side, as happens
                    // occasionally, around once per week according to logs.
                    try {
                        // Get document from Zustand store instead of Redux state
                        const currentSnapshot = services.documentStore.getState().document!;
                        const latestSnapshot = changeHistoryState.latestSnapshot;
                        if (!latestSnapshot) {
                            throw new Error("No latestSnapshot");
                        }
                        services.documentStore.getState().restoreToDocumentVersion(latestSnapshot, currentSnapshot);
                        // Hide change history panel after restoring.
                        services.useDocumentUiStore.getState().hideChangeHistoryPanel();
                        success = true;
                    } catch (error2) {
                        if (error2 instanceof Error) {
                            error2.message = `Unable to restore version: ${error2.message}`;
                            error2.cause = error1;
                        }
                        FluxLogger.captureError(error2, {
                            notifyUserMessage: snackbarMessageHelper(
                                "Unable to restore version. Please",
                                R.externalUrls.marketing.feedback,
                                "report a bug",
                                "and we'll alert you when it's resolved.",
                            ),
                        });
                    }
                } finally {
                    services.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.changeHistory, {
                        action: TrackingEventActions.changeHistory.restore,
                        success,
                        currentChangeId,
                        latestChangeUid,
                        currentChangeTimestamp: currentChange.actionTimestamp,
                        latestChangeTimestamp: latestChange.actionTimestamp,
                        isCodeVersionEqual: currentChange.actionCodeVersion === latestChange.actionCodeVersion,
                        isUserEqual: currentChange?.actionUserUid === latestChange?.actionUserUid,
                    });
                }
            }
        }
        PerformanceLogger.getInstance().stopPerformanceTrace(trace);
    },

    /**
     * Reverts or forwards the document to a specific change in the change history.
     * This is a regular function that takes FluxServices directly (not a Redux thunk).
     */
    revertOrForwardChanges: (
        targetChangeId: string,
        clearCurrentChangeId: boolean | undefined,
        services: FluxServices,
    ): void => {
        // Read from Zustand store instead of Redux to get the latest state
        const changeHistoryState = services.changeHistoryStore.getState();

        let success: boolean | undefined = false;
        try {
            success = _revertOrForwardChanges(changeHistoryState, targetChangeId, services, clearCurrentChangeId);
        } catch (error1) {
            if (error1 instanceof UndefinedChangeHistoryEntryError) {
                FluxLogger.captureError(error1, {
                    notifyUserMessage: snackbarMessageHelper(
                        "Unable to find change. Please",
                        R.externalUrls.marketing.feedback,
                        "report a bug",
                        "and we'll alert you when it's resolved.",
                    ),
                });
                return;
            }

            if (error1 instanceof Error) {
                const warning = `Unable to load version: "${error1.message}". Attempting to reconstruct project server-side.`;
                captureMessage(warning, {level: "warning"});
                if (!isProductionEnvironment()) {
                    // eslint-disable-next-line no-console
                    console.warn(warning);
                }
            }

            // QUESTION: for perf, use existing snapshot data if available?
            // NOTE: it is important we do callbacks and not make this function
            // async to avoid possible concurrency issues
            services.functionsAdapter
                .reconstructProject({
                    // Get document from Zustand store instead of Redux state
                    documentUid: services.documentStore.getState().document?.uid!,
                    actionRecordUid: targetChangeId,
                })
                .then((result) => {
                    services.documentStore.getState().setDocumentDataFromSnapshot(result.data);
                    // Update Zustand changeHistory store (Redux changeHistory slice has been removed)
                    services.changeHistoryStore.getState().setCurrentChangeId(targetChangeId);
                })
                .catch((error2) => {
                    if (error1 instanceof Error && error2 instanceof Error) {
                        error2.message = `Unable to load version by applying action records client-side (1) or by reconstructing the version server-side (2). The two error messages are: (1) "${error1.message}" and (2) "${error2.message}"`;
                        error2.cause = error1;
                    }
                    FluxLogger.captureError(error2, {
                        notifyUserMessage: snackbarMessageHelper(
                            "Unable to load version. Please",
                            R.externalUrls.marketing.feedback,
                            "report a bug",
                            "and we'll alert you when it's resolved.",
                        ),
                    });
                });
        } finally {
            const currentChangeId = changeHistoryState.currentChangeId;
            const currentChange = changeHistoryState.changeHistoryEntries[currentChangeId];
            const targetChange = changeHistoryState.changeHistoryEntries[targetChangeId];
            services.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.changeHistory, {
                action: TrackingEventActions.changeHistory.revertOrForward,
                success,
                currentChangeId,
                targetChangeId,
                currentChangeTimestamp: currentChange?.actionTimestamp,
                targetChangeTimestamp: targetChange?.actionTimestamp,
                isTargetChangeBeforeCurrentChange:
                    targetChange && currentChange
                        ? targetChange.actionTimestamp < currentChange.actionTimestamp
                            ? true
                            : false
                        : undefined,
                isCodeVersionEqual: currentChange?.actionCodeVersion === targetChange?.actionCodeVersion,
                isUserEqual: currentChange?.actionUserUid === targetChange?.actionUserUid,
            });
        }
    },
};
